// React core and hooks
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Redux
import { selectPatientId } from "../../../../../../redux/features/patientState/patientStateSlice";
import {
  setActiveMenu,
  setActivePage,
} from "../../../../../../redux/features/applicationState/applicationStateSlice";

// API configuration
import useAxios from "../../../../../../hooks/useAxios";
import multipartFormatAuth from "../../../../../../apis/multipartFormatAuth";
import jsonFormatAuth from "../../../../../../apis/jsonFormatAuth";

// API helpers
import {
  processPsoriasisSegmentationCalculationFormData,
  processPsoriasisSegmentationSaveFormData,
  sanitizePsoriasisSegmentationCalculationResultResponse,
} from "../../../../../../helpers/apiUtils/psoriasisSegmentationUtils";
import {
  clearComparisonItemsImage,
  sanitizeCeleryResponse,
  updateComparisonItemsPresigned,
} from "../../../../../../helpers/apiUtils/commonUtils";
import { entrySentResponse } from "./apiResponseHandlers/entry_sent_Response";

// Custom components
import ImageViewModal from "../../../../../../components/ImageViewModal/ImageViewModal";
import ComparisonModal from "../../../../../../components/ComparisonModal/ComparisonModal";
import EntryScreenTemplate from "../../../../../../components/EntryScreenTemplate/EntryScreenTemplate";
import ComparisonMenu from "../../../../../../components/ComparisonMenu/ComparisonMenu";
import ImageViewer from "../../../../../../components/ImageViewer/ImageViewer";
import ComparisonCard from "../../../../../../components/ComparisonCard/ComparisonCard";
import CalculationButton from "../../../../../../components/CalculationButton/CalculationButton";
import EntryBrowser from "../../../../../../components/EntryBrowser/EntryBrowser";
import EntryBrowserModal from "../../../../../../components/EntryBrowserModal/EntryBrowserModal";
import ProgressBar from "../../../../../../components/ProgressBar/ProgressBar";
import PsoriasisSegmentationCalculatedResults from "../../../../../../components/entryCalculatedResults/PsoriasisSegmentationCalculatedResults/PsoriasisSegmentationCalculatedResults";

// Standard objects
import { baseComparisonItem } from "../../../../../../helpers/baseObjects/baseComparisonItem";
import { celeryProgressObject } from "../../../../../../helpers/baseObjects/celeryProgressObject";

// Other utilities
import toastManager from "../../../../../../helpers/toastManager";
import cloneDeep from "lodash.clonedeep";
import _ from "lodash";
import { useHandleImageChange } from "../../../../../../hooks/useHandleImageChangeNew";
import { handleMenuRedirectPairs } from "../../../../../../helpers/entryUtils/handleMenuRedirectPairs";

// Entry helper functions
import { celeryStatusChecker } from "../../../../../../helpers/entryUtils/celeryProgressDetectors";
import { manageTagsSentResponse } from "../../../../../../helpers/entryUtils/manageTagsSentResponse";
import {
  getCeleryStatus,
  retryCeleryStatus,
} from "../../../../../../helpers/entryUtils/celeryStatusFunctions";
import { validateSave } from "../../../../../../helpers/entryUtils/validateSave";
import { validateAllowCalculation } from "../../../../../../helpers/entryUtils/validateAllowCalculation";
import { getPresignedUrls } from "../../../../../../helpers/entryUtils/getPresignedUrls";
import { managePresignedUrlsSent } from "../../../../../../helpers/entryUtils/managePresignedUrlsSent";
import { handleRapidEntrySave } from "../../../../../../helpers/rapidPatientHandler";
import { processRapidEntrySave } from "../../../../../../helpers/entryUtils/processRapidEntrySave";

// import rapidPatientHandler from "../../../../../../helpers/rapidPatientHandler";

function PsoriasisSegmentationEntryScreen(props) {
  const {
    rapidEntry = false,

    rapidPatientUniqueId,
    setRapidPatientUniqueId,

    rapidPatientFullName,
    setRapidPatientFullName,

    selectedExistingPatientId,
    setSelectedExistingPatientId,
    rapidEntryAction,
    setRapidEntryAction,
    // Field warnings.
    rapidPatientUniqueIdWarning,
    setRapidPatientUniqueIdWarning,
    rapidPatientFullNameWarning,
    // setRapidPatientFullNameWarning,
  } = props;

  // Navigation and application state
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const patientId = useSelector(selectPatientId);
  const { id } = useParams();

  // API related
  const { response, error, axiosFetch } = useAxios();
  const apiUrl = `entries/psoriasis_segmentation_entry/${id}/`;

  // Entry data and related state
  const [entrySavedStatus, setEntrySavedStatus] = useState(false);
  const [entryData, setEntryData] = useState({});
  const [computeRequired, setComputeRequired] = useState(false);
  const [recomputeAllowed, setRecomputeAllowed] = useState(false);

  // Image items-related state
  const [comparisonItems, setComparisonItems] = useState([
    cloneDeep(baseComparisonItem),
    cloneDeep(baseComparisonItem),
  ]);
  const [initialComparisonItems, setInitialComparisonItems] = useState([
    cloneDeep(baseComparisonItem),
    cloneDeep(baseComparisonItem),
  ]);
  const [processedComparisonItems, setProcessedComparisonItems] = useState([]);

  // AppointmentDate-related state
  const [appointmentDate, setAppointmentDate] = useState("");
  const [initialAppointmentDate, setInitialAppointmentDate] = useState("");

  // Note-related state
  const [notes, setNotes] = useState("");
  const [initialNotes, setInitialNotes] = useState("");

  // Summary-related state
  const [summary, setSummary] = useState("");
  const [initialSummary, setInitialSummary] = useState("");
  // Tag-related state
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [initialTags, setInitialTags] = useState([]);

  // Image in image view modal
  const [modalImage, setModalImage] = useState(null);

  // Comparison modal
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState("split");
  const [touchControls, setTouchControls] = useState({
    split: false,
    opacity: false,
  });

  // Previous entires modal browser
  const [entryBrowserModalOpen, setEntryBrowserModalOpen] = useState(false);
  const [currentComparisonIndex, setCurrentComparisonIndex] = useState(0);
  const [comparisonItemLocation, setComparisonItemLocation] = useState({
    index: 0,
    fileKey: "fileOne",
  });

  // Calculation-related state
  // State for managing loaders for image cards
  const [imageProcessingStatuses, setImageProcessingStatuses] = useState([
    "noImageSelected",
    "noImageSelected",
  ]);

  // Semantic zoom coefficient related state
  const [semanticZC, setSemanticZC] = useState(0.4);
  const [initialSemanticZC, setInitialSemanticZC] = useState(0.4);
  const [processedSemanticZC, setProcessedSemanticZC] = useState(0.4);

  // Classification zoom coefficient related state
  const [classificationZC, setClassificationZC] = useState(0.4);
  const [initialClassificationZC, setInitialClassificationZC] = useState(0.4);
  const [processedClassificationZC, setProcessedClassificationZC] =
    useState(0.4);

  // Celery task related state
  const [calculationProgress, setCalculationProgress] =
    useState(celeryProgressObject);
  const [celeryID, setCeleryID] = useState("");

  // Psoriasis calculation result state
  const [
    psoriasisSegmentationCalculationData,
    setPsoriasisSegmentationCalculationData,
  ] = useState({
    classificationZC: 0.5,
    semanticZC: 0.5,

    // # Affected Percentages of whole body, front and back images.
    affectedPercentageBody: 0,
    affectedPercentageFront: 0,
    affectedPercentageBack: 0,

    // # Overall PASI score
    pasiScore: 0,

    // # Data for standard PASI Scoring.
    // # 1: Head.
    headAffectedPercentage: 0,
    headAreaScore: 0,
    headSegmentScore: 0,
    headErythema: 0,
    headInduration: 0,
    headDesquamation: 0,

    // # 2: Torso / Trunk.
    torsoAffectedPercentage: 0,
    torsoAreaScore: 0,
    torsoSegmentScore: 0,
    torsoErythema: 0,
    torsoInduration: 0,
    torsoDesquamation: 0,

    // # 3: Arms / Upper Extremities.
    armsAffectedPercentage: 0,
    armsAreaScore: 0,
    armsSegmentScore: 0,
    armsErythema: 0,
    armsInduration: 0,
    armsDesquamation: 0,

    // # 4: Legs / Lower Extremities.
    legsAffectedPercentage: 0,
    legsAreaScore: 0,
    legsSegmentScore: 0,
    legsErythema: 0,
    legsInduration: 0,
    legsDesquamation: 0,
  });

  // FUNCTIONS START
  useEffect(() => {
    // Obtaining data from API on initial page load
    getEntryData();
  }, []);

  // Detect image presence and manage loaders on cards
  useHandleImageChange({
    comparisonItems,
    imageProcessingStatuses,
    calculationProgress,
    setImageProcessingStatuses,
    setComparisonItems,
    baseComparisonItem,
    index: 0,
  });

  useHandleImageChange({
    comparisonItems,
    imageProcessingStatuses,
    calculationProgress,
    setImageProcessingStatuses,
    setComparisonItems,
    baseComparisonItem,
    index: 1,
  });

  // Detect if entry needs to be re-computed due to image changes
  useEffect(() => {
    if (
      !_.isEqual(
        comparisonItems[0]?.fileOne,
        processedComparisonItems[0]?.fileOne
      ) ||
      !_.isEqual(
        comparisonItems[0]?.fileTwo,
        processedComparisonItems[0]?.fileTwo
      ) ||
      !_.isEqual(
        comparisonItems[1]?.fileOne,
        processedComparisonItems[1]?.fileOne
      ) ||
      !_.isEqual(
        comparisonItems[1]?.fileTwo,
        processedComparisonItems[1]?.fileTwo
      )
    ) {
      setComputeRequired(true);
    } else {
      setComputeRequired(false);
    }
  }, [comparisonItems, processedComparisonItems]);

  // Detect if zoom coefficients are currently different form those which were used for calculation
  useEffect(() => {
    if (
      (typeof semanticZC === "number"
        ? semanticZC.toFixed(2)
        : Number(semanticZC).toFixed(2)) !==
        (typeof processedSemanticZC === "number"
          ? processedSemanticZC.toFixed(2)
          : Number(processedSemanticZC).toFixed(2)) ||
      (typeof classificationZC === "number"
        ? classificationZC.toFixed(2)
        : Number(classificationZC).toFixed(2)) !==
        (typeof processedClassificationZC === "number"
          ? processedClassificationZC.toFixed(2)
          : Number(processedClassificationZC).toFixed(2))
    ) {
      setRecomputeAllowed(true);
    } else {
      setRecomputeAllowed(false);
    }
  }, [
    semanticZC,
    processedSemanticZC,
    classificationZC,
    processedClassificationZC,
  ]);

  // * Primary entry function
  const getEntryData = () => {
    axiosFetch({
      axiosInstance: multipartFormatAuth(),
      method: "GET",
      url: apiUrl,
      sendSignal: false,
      requestConfig: {},
    });
  };

  const calculateEntry = () => {
    if (!validateAllowCalculation({ computeRequired, recomputeAllowed })) {
      return;
    }

    const formData = new FormData();

    formData.append("entry_action", "calculate");
    formData.append("is_rapid_entry", rapidEntry);

    const isProcessingSuccessful =
      processPsoriasisSegmentationCalculationFormData({
        formData: formData,
        comparisonItems: comparisonItems,
        classificationZC: classificationZC,
        semanticZC: semanticZC,
      });
    if (!isProcessingSuccessful) {
      return;
    }

    axiosFetch({
      axiosInstance: multipartFormatAuth(),
      method: "POST",
      url: apiUrl,
      sendSignal: false,
      requestConfig: formData,
    }).catch((error) => {
      // Handle errors here
      toastManager(
        "error",
        "Error",
        "There was an error trying to perform the calculation. Please try again."
      );
    });
  };

  const saveEntry = () => {
    if (
      !validateSave({ calculationProgress, hasDataChanged, computeRequired })
    ) {
      return;
    }

    const formData = new FormData();

    formData.append("appointment_date", appointmentDate);
    formData.append("notes", notes);
    formData.append("summary", summary);
    formData.append("classification_tags", JSON.stringify(tags));

    formData.append("entry_action", "save");
    formData.append("is_rapid_entry", rapidEntry);

    processPsoriasisSegmentationSaveFormData({
      formData: formData,
      comparisonItems: comparisonItems,
    });

    const isProcessRapidEntrySaveSuccessful = processRapidEntrySave({
      formData,
      rapidEntry,
      rapidEntryAction,
      rapidPatientUniqueId,
      rapidPatientFullName,
      setRapidPatientUniqueIdWarning,
      selectedExistingPatientId,
    });
    if (!isProcessRapidEntrySaveSuccessful) {
      return;
    }

    axiosFetch({
      axiosInstance: multipartFormatAuth(),
      method: "POST",
      url: apiUrl,
      sendSignal: false,
      requestConfig: formData,
    }).catch((error) => {
      // Handle errors here
      toastManager(
        "error",
        "Error",
        "There was an error sending the entry data. Please try again."
      );
    });
  };

  const deleteEntry = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "DELETE",
      url: apiUrl,
      sendSignal: false,
      requestConfig: {
        // Attach delete_confirmation and if this is a rapid_entry
        data: { delete_confirmation: true, is_rapid_entry: rapidEntry },
      },
    });
  };

  // * Secondary entry functions
  const hasDataChanged = () => {
    return (
      JSON.stringify(initialComparisonItems) !==
        JSON.stringify(comparisonItems) ||
      appointmentDate !== initialAppointmentDate ||
      notes !== initialNotes ||
      summary !== initialSummary ||
      JSON.stringify(tags) !== JSON.stringify(initialTags) ||
      (typeof semanticZC === "number"
        ? semanticZC.toFixed(2)
        : Number(semanticZC).toFixed(2)) !==
        (typeof initialSemanticZC === "number"
          ? initialSemanticZC.toFixed(2)
          : Number(initialSemanticZC).toFixed(2)) ||
      (typeof classificationZC === "number"
        ? classificationZC.toFixed(2)
        : Number(classificationZC).toFixed(2)) !==
        (typeof initialClassificationZC === "number"
          ? initialClassificationZC.toFixed(2)
          : Number(initialClassificationZC).toFixed(2))
    );
  };

  const handleSuccessfulSave = () => {
    toastManager(
      "success",
      "Entry Saved Successfully!",
      "All entry photos and data have been saved to the server."
    );
    setEntrySavedStatus(true);
    // Update values to detect changes
    setInitialTags([...tags]);
    setInitialAppointmentDate(appointmentDate);
    setInitialSummary(summary);
    setInitialNotes(notes);
    setInitialComparisonItems(cloneDeep(comparisonItems));
    setInitialSemanticZC(semanticZC);
    setProcessedSemanticZC(semanticZC);
    setInitialClassificationZC(classificationZC);
    setProcessedClassificationZC(classificationZC);
  };

  const handlePageLeave = () => {
    const leaveCondition = patientId || !rapidEntry;
    const activeLink = leaveCondition
      ? `/entries/psoriasis_segmentation_menu/${patientId}`
      : "/";
    navigate(activeLink);
    dispatch(setActiveMenu(leaveCondition ? "patients" : "home"));
    dispatch(setActivePage(activeLink));
  };

  const handleMenuRedirect = () =>
    handleMenuRedirectPairs({
      hasDataChanged,
      comparisonItems,
      deleteEntry,
      entrySavedStatus,
      handlePageLeave,
    });

  const getTagSuggestions = () => {
    axiosFetch({
      axiosInstance: jsonFormatAuth(),
      method: "GET",
      url: "tags/autosuggestions/",
      sendSignal: false,
      requestConfig: {},
    });
  };
  // FUNCTIONS END

  // API START
  useEffect(() => {
    if (response) {
      // console.log("Response:");
      // console.log(response);

      const actionCode = response?.action_code;

      if (actionCode === "presigned_urls_sent") {
        const sanitizedUrlsList = managePresignedUrlsSent({ response });

        const presignedUrlImages = [];
        sanitizedUrlsList.forEach((sanitizedUrl) => {
          if (sanitizedUrl.imageName === "overlay_front") {
            presignedUrlImages.push({
              index: 0, // assign index 1 to overlay_front
              presignedUrl: sanitizedUrl.presignedUrl,
            });
          } else if (sanitizedUrl.imageName === "overlay_back") {
            presignedUrlImages.push({
              index: 1, // assign index 2 to overlay_back
              presignedUrl: sanitizedUrl.presignedUrl,
            });
          }
        });

        updateComparisonItemsPresigned({
          setComparisonItems,
          setProcessedComparisonItems,
          presignedUrlImages,
        });
      }

      if (actionCode === "entry_sent") {
        // Entry data received
        entrySentResponse({
          response,
          getTagSuggestions,
          setEntryData,
          setCeleryID,
          setCalculationProgress,
          setComparisonItems,
          setInitialComparisonItems,
          setProcessedComparisonItems,
          setAppointmentDate,
          setInitialAppointmentDate,
          setTags,
          setInitialTags,
          setSummary,
          setInitialSummary,
          setNotes,
          setInitialNotes,
          setSemanticZC,
          setInitialSemanticZC,
          setProcessedSemanticZC,
          setClassificationZC,
          setInitialClassificationZC,
          setProcessedClassificationZC,
          setPsoriasisSegmentationCalculationData,
          getCeleryStatus,
          axiosFetch,
        });
      }

      // Calculation successfully started
      if (actionCode === "calculation_started") {
        const celeryTaskIdResponse = response?.payload?.celery_task_id;
        if (celeryTaskIdResponse) {
          setCeleryID(celeryTaskIdResponse);
          getCeleryStatus({ axiosFetch, celeryTaskId: celeryTaskIdResponse });
        }
      }

      // Entry successfully saved
      if (actionCode === "entry_saved") {
        handleSuccessfulSave();
      }

      // Rapid entry successfully saved
      if (
        actionCode === "rapid_entry_saved_to_new" ||
        actionCode === "rapid_entry_saved_to_existing"
      ) {
        handleRapidEntrySave({
          response,
          pageLink: `/entries/psoriasis_segmentation_entry/${id}`,
          activePage: `/entries/psoriasis_segmentation_menu/`,
          navigate,
          entryType: "psoriasis",
        });
      }

      // Entry successfully deleted
      if (actionCode === "entry_deleted") {
        toastManager(
          "success",
          "Entry Deleted Successfully!",
          "All entry photos and data have been removed from the server."
        );
        handlePageLeave();
      }

      // Tag suggestions received
      if (actionCode === "tags_sent") {
        manageTagsSentResponse({ response, setTagSuggestions });
      }

      // Detect celery progress
      if (
        (response && response.state === "PENDING") ||
        response.state === "PROGRESS" ||
        response.state === "FAILURE"
      ) {
        celeryStatusChecker({
          response,
          sanitizeCeleryResponse,
          setCalculationProgress,
          retryCeleryStatus,
          axiosFetch,
          celeryID,
        });
      }

      if (response && response.state === "SUCCESS") {
        // console.log("Task is done!");
        const sanitizedCeleryResponse = sanitizeCeleryResponse({
          celeryProgressResponse: response,
        });
        setCalculationProgress(sanitizedCeleryResponse);
        const celeryResultResponse = response?.result;
        if (celeryResultResponse) {
          // Sanitize celery result
          const sanitizedPsoriasisSegmentationCalculationResultResponse =
            sanitizePsoriasisSegmentationCalculationResultResponse({
              psoriasisSegmentationCalculationResultResponse:
                celeryResultResponse,
            });

          setPsoriasisSegmentationCalculationData((prevState) => ({
            ...prevState,

            classificationZC:
              sanitizedPsoriasisSegmentationCalculationResultResponse.classification_zc,
            semanticZC:
              sanitizedPsoriasisSegmentationCalculationResultResponse.semantic_zc,

            // # Affected Percentages of whole body, front and back images.
            affectedPercentageBody:
              sanitizedPsoriasisSegmentationCalculationResultResponse.affected_percentage_body,
            affectedPercentageFront:
              sanitizedPsoriasisSegmentationCalculationResultResponse.affected_percentage_front,
            affectedPercentageBack:
              sanitizedPsoriasisSegmentationCalculationResultResponse.affected_percentage_back,

            // # Overall PASI score
            pasiScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.pasi_score,

            // # Data for standard PASI Scoring.
            // # 1: Head.
            headAffectedPercentage:
              sanitizedPsoriasisSegmentationCalculationResultResponse.head_affected_percentage,
            headAreaScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.head_area_score,
            headSegmentScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.head_segment_score,
            headErythema:
              sanitizedPsoriasisSegmentationCalculationResultResponse.head_erythema,
            headInduration:
              sanitizedPsoriasisSegmentationCalculationResultResponse.head_induration,
            headDesquamation:
              sanitizedPsoriasisSegmentationCalculationResultResponse.head_desquamation,

            // # 2: Torso / Trunk.
            torsoAffectedPercentage:
              sanitizedPsoriasisSegmentationCalculationResultResponse.torso_affected_percentage,
            torsoAreaScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.torso_area_score,
            torsoSegmentScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.torso_segment_score,
            torsoErythema:
              sanitizedPsoriasisSegmentationCalculationResultResponse.torso_erythema,
            torsoInduration:
              sanitizedPsoriasisSegmentationCalculationResultResponse.torso_induration,
            torsoDesquamation:
              sanitizedPsoriasisSegmentationCalculationResultResponse.torso_desquamation,

            // # 3: Arms / Upper Extremities.
            armsAffectedPercentage:
              sanitizedPsoriasisSegmentationCalculationResultResponse.arms_affected_percentage,
            armsAreaScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.arms_area_score,
            armsSegmentScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.arms_segment_score,
            armsErythema:
              sanitizedPsoriasisSegmentationCalculationResultResponse.arms_erythema,
            armsInduration:
              sanitizedPsoriasisSegmentationCalculationResultResponse.arms_induration,
            armsDesquamation:
              sanitizedPsoriasisSegmentationCalculationResultResponse.arms_desquamation,

            // # 4: Legs / Lower Extremities.
            legsAffectedPercentage:
              sanitizedPsoriasisSegmentationCalculationResultResponse.legs_affected_percentage,
            legsAreaScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.legs_area_score,
            legsSegmentScore:
              sanitizedPsoriasisSegmentationCalculationResultResponse.legs_segment_score,
            legsErythema:
              sanitizedPsoriasisSegmentationCalculationResultResponse.legs_erythema,
            legsInduration:
              sanitizedPsoriasisSegmentationCalculationResultResponse.legs_induration,
            legsDesquamation:
              sanitizedPsoriasisSegmentationCalculationResultResponse.legs_desquamation,
          }));

          // Update zoom coefficients to show which values were used to process images
          setProcessedSemanticZC(
            sanitizedPsoriasisSegmentationCalculationResultResponse.semantic_zc
          );
          setProcessedClassificationZC(
            sanitizedPsoriasisSegmentationCalculationResultResponse.classification_zc
          );

          // Clear old image from card
          clearComparisonItemsImage({
            setComparisonItems: setComparisonItems,
            imageIndex: 0,
            imageKey: "fileTwo",
          });
          clearComparisonItemsImage({
            setComparisonItems: setComparisonItems,
            imageIndex: 1,
            imageKey: "fileTwo",
          });

          // Send request for presigned URL
          const overlayFrontKey =
            sanitizedPsoriasisSegmentationCalculationResultResponse.overlay_front;
          const overlayBackKey =
            sanitizedPsoriasisSegmentationCalculationResultResponse.overlay_back;

          const presignedUrlList = [];

          if (overlayFrontKey) {
            presignedUrlList.push({
              image_name: "overlay_front",
              image_path: overlayFrontKey,
            });
          }
          if (overlayBackKey) {
            presignedUrlList.push({
              image_name: "overlay_back",
              image_path: overlayBackKey,
            });
          }

          getPresignedUrls({ axiosFetch, imageList: presignedUrlList });
        }
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // console.log("Error:");
      // console.log(error);

      const actionCode = error?.action_code;

      // Entry does not exist.
      if (actionCode === "entry_not_exist" || error?.pk) {
        toastManager(
          "error",
          "Entry does not exist!",
          "Entry not found or not owned by the user."
        );
        handlePageLeave();
      }

      if (error?.rapid_patient_unique_id?.action_code === "unique_id_taken") {
        toastManager(
          "warning",
          "Patient ID taken!",
          "This Unique Patient ID is already assigned to another patient. Use a different one."
        );
        setRapidPatientUniqueIdWarning(true);
      }
    }
  }, [error]);
  // API END

  return (
    <EntryScreenTemplate
      entryTitle={"Psoriasis Segmentation Calculation Entry"}
      handleMenuRedirect={handleMenuRedirect}
      appointmentDate={appointmentDate}
      setAppointmentDate={setAppointmentDate}
      suggestionList={tagSuggestions}
      tags={tags}
      setTags={setTags}
      notes={notes}
      setNotes={setNotes}
      summary={summary}
      setSummary={setSummary}
      saveEntryFunction={saveEntry}
      deleteEntryFunction={deleteEntry}
      entryChanged={hasDataChanged()}
      entryData={entryData}
      getEntryData={getEntryData}
      // Rapid entry
      rapidEntry={rapidEntry}
      rapidPatientUniqueId={rapidPatientUniqueId}
      setRapidPatientUniqueId={setRapidPatientUniqueId}
      rapidPatientFullName={rapidPatientFullName}
      setRapidPatientFullName={setRapidPatientFullName}
      selectedExistingPatientId={selectedExistingPatientId}
      setSelectedExistingPatientId={setSelectedExistingPatientId}
      setRapidEntryAction={setRapidEntryAction}
      // Field warnings.
      rapidPatientUniqueIdWarning={rapidPatientUniqueIdWarning}
      rapidPatientFullNameWarning={rapidPatientFullNameWarning}
    >
      <div className="flex flex-col items-center justify-center w-full h-full Xbg-red-500">
        {/* <h5>comparisonItems</h5> */}
        {/* <h6>{JSON.stringify(comparisonItems)}</h6> */}
        {/* <h5>processedComparisonItems</h5> */}
        {/* <h6>{JSON.stringify(processedComparisonItems)}</h6> */}
        {/* <h6>{JSON.stringify(celeryStatus)}</h6> */}
        {/* <h6>{JSON.stringify(calculationProgress)}</h6> */}
        {/* <h6>{JSON.stringify(`re - computeRequired: ${computeRequired}`)}</h6>
        <h6>{JSON.stringify(`re - computeAllowed: ${recomputeAllowed}`)}</h6> */}

        {modalImage && (
          <ImageViewModal exitFunction={setModalImage} setValue={null}>
            <ImageViewer modalImage={modalImage} />
          </ImageViewModal>
        )}

        {entryBrowserModalOpen && (
          <EntryBrowserModal
            exitFunction={setEntryBrowserModalOpen}
            setValue={false}
          >
            <EntryBrowser
              patientId={patientId}
              comparisonItems={comparisonItems}
              setComparisonItems={setComparisonItems}
              comparisonItemLocation={comparisonItemLocation}
              setEntryBrowserModalOpen={setEntryBrowserModalOpen}
            />
          </EntryBrowserModal>
        )}

        {compareModalOpen && (
          <ComparisonModal
            comparisonItems={comparisonItems}
            setComparisonItems={setComparisonItems}
            currentComparisonIndex={currentComparisonIndex}
            exitFunction={setCompareModalOpen}
            setValue={false}
            currentMenu={currentMenu}
            setCurrentMenu={setCurrentMenu}
            touchControls={touchControls}
            setTouchControls={setTouchControls}
          >
            <ComparisonMenu
              setComparisonItems={setComparisonItems}
              comparisonItems={comparisonItems}
              currentComparisonIndex={currentComparisonIndex}
              currentMenu={currentMenu}
              touchControls={touchControls}
            />
          </ComparisonModal>
        )}

        {comparisonItems.map((comparisonItem, index) => {
          return (
            <ComparisonCard
              key={`comparisonPair-${index}`}
              entryType={"compute"}
              calculationProgress={calculationProgress}
              imageProcessingStatuses={imageProcessingStatuses}
              index={index}
              setCurrentComparisonIndex={setCurrentComparisonIndex}
              baseComparisonItem={baseComparisonItem}
              comparisonItems={comparisonItems}
              comparisonItem={comparisonItem}
              setComparisonItems={setComparisonItems}
              setCurrentMenu={setCurrentMenu}
              setModalImage={setModalImage}
              compareModalOpen={compareModalOpen}
              setCompareModalOpen={setCompareModalOpen}
              entryBrowserModalOpen={entryBrowserModalOpen}
              setEntryBrowserModalOpen={setEntryBrowserModalOpen}
              comparisonItemLocation={comparisonItemLocation}
              setComparisonItemLocation={setComparisonItemLocation}
              rapidEntry={rapidEntry}
            />
          );
        })}

        <CalculationButton
          semanticZC={semanticZC}
          setSemanticZC={setSemanticZC}
          classificationZC={classificationZC}
          setClassificationZC={setClassificationZC}
          entryType={"segmentation"}
          comparisonItems={comparisonItems}
          imageProcessingStatuses={imageProcessingStatuses}
          calculateEntry={calculateEntry}
          buttonPrompt={"Calculate"}
        />

        <ProgressBar calculationProgress={calculationProgress} />

        <PsoriasisSegmentationCalculatedResults
          calculationProgress={calculationProgress}
          psoriasisSegmentationCalculationData={
            psoriasisSegmentationCalculationData
          }
        />
      </div>
    </EntryScreenTemplate>
  );
}

export default PsoriasisSegmentationEntryScreen;
